// NEW_FILE_JUSTIFIED: Dedicated page for bulk renewal import — no existing page handles multi-file bulk extraction flow
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ are one level deeper than src/pages/, requiring three levels up to reach convex/_generated/
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useBulkImport } from '@/hooks/useBulkImport';
import { BulkImportUploadStep } from '@/components/insurance/bulk-import/UploadStep';
import { BulkImportExtractionStep } from '@/components/insurance/bulk-import/ExtractionStep';
import { BulkImportReviewStep } from '@/components/insurance/bulk-import/ReviewStep';
import type { IOrgInsuranceType } from '@/@types/insurance';

export function BulkRenewalImportPage() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();

  const orgTypes = useQuery(
    api.insurance.types.listOrgTypes,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const typeList: IOrgInsuranceType[] = orgTypes ?? [];

  const {
    step,
    uploadedFileNames,
    matchResults,
    error,
    progress,
    hasUnassignedTypes,
    uploadAndExtract,
    updateAction,
    updateActions,
    updateType,
    execute,
    reset,
  } = useBulkImport(currentOrg?._id ?? null, typeList);

  const handleExecute = useCallback(() => {
    void execute();
  }, [execute]);

  const handleCancel = useCallback(() => {
    reset();
    navigate('/insurance/policies');
  }, [reset, navigate]);

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to use bulk import.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-4xl mx-auto py-6 px-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/insurance/policies')}>
            <ArrowLeft className="w-4 h-4 mr-1" aria-hidden="true" />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">Bulk Renewal Import</h1>
            <p className="text-sm text-muted-foreground">
              Upload provider renewal lists to create or update policies in bulk
            </p>
          </div>
        </div>
      </div>

      {step === 'upload' && (
        <BulkImportUploadStep
          onFilesSelected={files => void uploadAndExtract(files)}
          isUploading={false}
        />
      )}

      {step === 'extracting' && <BulkImportExtractionStep fileNames={uploadedFileNames} />}

      {step === 'review' && (
        <BulkImportReviewStep
          matchResults={matchResults}
          orgTypes={typeList}
          onActionChange={updateAction}
          onBatchActionChange={updateActions}
          onTypeChange={updateType}
          onExecute={handleExecute}
          onCancel={handleCancel}
          isProcessing={false}
          hasUnassignedTypes={hasUnassignedTypes}
        />
      )}

      {step === 'processing' && (
        <div className="flex flex-col items-center gap-4 py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <p className="text-sm text-muted-foreground">Creating and updating policies...</p>
        </div>
      )}

      {step === 'completed' && (
        <div className="flex flex-col items-center gap-4 py-12 text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-500" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-foreground">Import Complete</h2>
          <div className="flex items-center gap-3">
            <Badge variant="secondary">{progress.totalCreated} created</Badge>
            <Badge variant="secondary">{progress.totalEnriched} enriched</Badge>
            {progress.totalSkipped > 0 && (
              <Badge variant="outline">{progress.totalSkipped} skipped</Badge>
            )}
            {progress.totalErrors > 0 && (
              <Badge variant="destructive">{progress.totalErrors} errors</Badge>
            )}
          </div>
          {error && <div className="text-xs text-destructive mt-2 max-w-md">{error}</div>}
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={reset}>
              Import More
            </Button>
            <Button onClick={() => navigate('/insurance/policies')}>View Policies</Button>
          </div>
        </div>
      )}

      {step === 'error' && (
        <div className="flex flex-col items-center gap-4 py-12 text-center">
          <div className="rounded-full bg-destructive/10 p-3">
            <span className="text-2xl text-destructive">!</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground">Import Failed</h2>
          <p className="text-sm text-destructive max-w-md">{error}</p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => navigate('/insurance/policies')}>
              Back to Policies
            </Button>
            <Button onClick={reset}>Try Again</Button>
          </div>
        </div>
      )}
    </div>
  );
}
